// Cache drawing PDFs on first online open so they can be viewed offline later.
import { Filesystem, Directory } from "@capacitor/filesystem";
import { Capacitor } from "@capacitor/core";
import localforage from "localforage-esm";
import _ from "lodash";

const meta = localforage.createInstance({
  name: "LionOffline",
  storeName: "pdfs",
});

const pathForUrl = (url) => {
  const safe = String(url).replace(/[^a-zA-Z0-9._-]/g, "_").slice(-180);
  return `lion_offline_pdfs/${safe}`;
};

export const cachePdfFromUrl = async (url) => {
  if (!url || !Capacitor.isNativePlatform()) return null;
  try {
    const response = await fetch(url);
    if (!response.ok) return null;
    const blob = await response.blob();
    const buffer = await blob.arrayBuffer();
    const bytes = new Uint8Array(buffer);
    let binary = "";
    for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
    const base64 = btoa(binary);
    const path = pathForUrl(url);
    await Filesystem.writeFile({
      path,
      data: base64,
      directory: Directory.Data,
      recursive: true,
    });
    await meta.setItem(url, { path, cachedAt: Date.now() });
    return path;
  } catch (_e) {
    return null;
  }
};

export const getCachedPdfUri = async (url) => {
  if (!url) return null;
  const hit = await meta.getItem(url);
  if (!hit?.path) return null;
  try {
    const { uri } = await Filesystem.getUri({ path: hit.path, directory: Directory.Data });
    return Capacitor.convertFileSrc(uri);
  } catch (_e) {
    return null;
  }
};

export const openDrawingPdf = async (url) => {
  if (!url) return;
  if (!Capacitor.isNativePlatform()) {
    window.open(url, "_blank");
    return;
  }
  const cached = await getCachedPdfUri(url);
  if (cached) {
    window.open(cached, "_blank");
    return;
  }
  // Online path: open remote and cache in background
  window.open(url, "_blank");
  cachePdfFromUrl(url);
};

export const openDrawingPdfOrWarn = async (url, { offline } = {}) => {
  if (!url) return;
  const cached = await getCachedPdfUri(url);
  if (cached) {
    window.open(cached, "_blank");
    return;
  }
  if (offline) {
    if (window.f7?.dialog) {
      window.f7.dialog.alert("This PDF has not been downloaded yet and is not available offline.");
    } else {
      alert("This PDF has not been downloaded yet and is not available offline.");
    }
    return;
  }
  await openDrawingPdf(url);
};

// Latest revision PDFs for a drawings list — skip already-cached, stop if we drop offline.
export const prefetchLatestDrawingPdfs = async (drawings, { limit = 15, isOnline } = {}) => {
  const urls = [];
  _.each(drawings, (drawing) => {
    const revisions = drawing?.revisions_attributes || [];
    const latest = _.find(revisions, { latest: true }) || _.last(revisions);
    if (latest?.drawing_pdf_url) urls.push(latest.drawing_pdf_url);
  });

  for (const url of _.take(urls, limit)) {
    if (isOnline && !isOnline()) break;
    if (await getCachedPdfUri(url)) continue;
    await cachePdfFromUrl(url);
  }
};

export const clearPdfCache = async () => {
  await meta.clear();
  try {
    await Filesystem.rmdir({ path: "lion_offline_pdfs", directory: Directory.Data, recursive: true });
  } catch (_e) {
    // dir missing
  }
};
